import React, { useState, useMemo } from 'react';
import { severityColours } from '../utils/nodeTemplates';
import { SEVERITY_RANK } from '../../../types/categoricalReasoner';
import './ViolationsReportView.css';

/**
 * SeverityBadge -- coloured label for CRITICAL / HIGH / MEDIUM / LOW.
 */
function SeverityBadge({ severity }) {
  const sev = String(severity || 'UNKNOWN').toUpperCase();
  const colour = severityColours[sev] || severityColours.UNKNOWN;
  return (
    <span className="vr-sev-badge" style={{ background: colour }}>{sev}</span>
  );
}

/**
 * PredicateLine -- renders a single fired/unmet predicate row.
 */
function PredicateLine({ pred }) {
  const fired = !!pred.fired;
  const conf = typeof pred.confidence === 'number' ? pred.confidence.toFixed(2) : null;
  return (
    <div className={`vr-pred ${fired ? 'fired' : 'unmet'}`}>
      <span className="vr-pred-kind">{pred.kind}</span>
      {pred.anchor && <span className="vr-pred-anchor">{pred.anchor}</span>}
      <span className="vr-pred-detail">
        {pred.kind === 'rule' && (
          <>
            <code>{pred.case_query}</code>
            {pred.graph_ref && <span className="vr-graph-ref"> @ {pred.graph_ref}</span>}
          </>
        )}
        {pred.kind === 'case_field' && (
          <>
            <code>{pred.case_path}</code>
            {pred.op && <span> {pred.op}</span>}
            {pred.value !== undefined && <span> {JSON.stringify(pred.value)}</span>}
          </>
        )}
        {pred.kind === 'case_nl' && <code>{pred.case_query}</code>}
      </span>
      {conf !== null && <span className="vr-pred-conf">conf {conf}</span>}
      {pred.evidence_quote && (
        <div className="vr-pred-quote">&ldquo;{pred.evidence_quote}&rdquo;</div>
      )}
    </div>
  );
}

/**
 * InstanceCard -- renders one fired binding within a pack.
 */
function InstanceCard({ instance }) {
  const binding = instance.binding || {};
  const summary = binding.name
    || binding.entity_id
    || binding.txn_id
    || binding.relationship_id
    || JSON.stringify(binding).slice(0, 80);
  const firedPredicates = Array.isArray(instance.fired_predicates)
    ? instance.fired_predicates
    : [];
  const unmet = Array.isArray(instance.unmet_predicates)
    ? instance.unmet_predicates
    : [];
  return (
    <div className={`vr-inst ${instance.fired ? 'inst-fired' : 'inst-unmet'}`}>
      <div className="vr-inst-header">
        <span className="vr-inst-status">{instance.fired ? 'FIRED' : 'UNMET'}</span>
        <span className="vr-inst-binding">{instance.binding_kind || 'binding'}: <strong>{summary}</strong></span>
        {typeof instance.score === 'number' && (
          <span className="vr-inst-score">score {instance.score.toFixed(2)}</span>
        )}
      </div>
      {firedPredicates.length > 0 && (
        <div className="vr-pred-list">
          <div className="vr-pred-list-title">Fired predicates</div>
          {firedPredicates.map((p, i) => <PredicateLine key={`f-${i}`} pred={p} />)}
        </div>
      )}
      {unmet.length > 0 && (
        <div className="vr-pred-list vr-unmet-list">
          <div className="vr-pred-list-title">Unmet predicates</div>
          {unmet.map((p, i) => <PredicateLine key={`u-${i}`} pred={p} />)}
        </div>
      )}
    </div>
  );
}

/**
 * ViolationCard -- collapsible card per pack.
 */
function ViolationCard({ violation, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const fired = violation.fired
    || (violation.instances || []).some(i => i.fired)
    || (violation.weighted_score && violation.weighted_score > 0);
  const instancesFired = (violation.instances || []).filter(i => i.fired).length;
  const totalInstances = (violation.instances || []).length;
  return (
    <div className={`vr-card ${fired ? 'fired' : 'unmet'}`}>
      <button
        className="vr-card-header"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <SeverityBadge severity={violation.severity} />
        <span className="vr-card-pack-id">{violation.pack_id}</span>
        {violation.pack_name && (
          <span className="vr-card-pack-name">{violation.pack_name}</span>
        )}
        <span className="vr-card-status">
          {fired ? `${instancesFired}/${totalInstances} fired` : 'unmet'}
        </span>
        {typeof violation.weighted_score === 'number' && (
          <span className="vr-card-score">score {violation.weighted_score.toFixed(2)}</span>
        )}
        <span className="vr-card-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="vr-card-body">
          {violation.description && (
            <p className="vr-card-desc">{violation.description}</p>
          )}
          {(violation.instances || []).map((inst, i) => (
            <InstanceCard key={i} instance={inst} />
          ))}
          {(violation.instances || []).length === 0 && (
            <p className="vr-card-empty">No bindings produced for this pack.</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ViolationsReportView
 *
 * Renders one card per pack returned by the categorical chain reasoner,
 * sorted by severity then weighted score. Skipped packs are collapsed
 * at the bottom.
 *
 * @param {Object} props
 * @param {import('../../../types/categoricalReasoner').ViolationsReport} props.report
 */
function ViolationsReportView({ report }) {
  const sorted = useMemo(() => {
    const v = Array.isArray(report?.violations) ? [...report.violations] : [];
    v.sort((a, b) => {
      const aFired = a.fired || (a.instances || []).some(i => i.fired) ? 1 : 0;
      const bFired = b.fired || (b.instances || []).some(i => i.fired) ? 1 : 0;
      if (aFired !== bFired) return bFired - aFired;
      const sevDiff =
        (SEVERITY_RANK[String(b.severity || '').toUpperCase()] || 0) -
        (SEVERITY_RANK[String(a.severity || '').toUpperCase()] || 0);
      if (sevDiff !== 0) return sevDiff;
      return (b.weighted_score || 0) - (a.weighted_score || 0);
    });
    return v;
  }, [report]);

  if (!report) {
    return <div className="vr-empty">No violations report available.</div>;
  }

  const skipped = Array.isArray(report.skipped_packs) ? report.skipped_packs : [];

  return (
    <div className="violations-report">
      <div className="vr-summary">
        <span>Domain: <strong>{report.domain || 'AML'}</strong></span>
        <span>Packs fired: <strong>{report.total_packs_fired ?? 0}/{report.total_packs_loaded ?? 0}</strong></span>
        <span>Instances: <strong>{report.total_instances_fired ?? 0}</strong></span>
      </div>

      {sorted.length === 0 && (
        <div className="vr-empty">No packs returned by the chain reasoner.</div>
      )}

      {sorted.map((v, i) => (
        <ViolationCard
          key={v.pack_id || i}
          violation={v}
          defaultOpen={(v.fired || (v.instances || []).some(inst => inst.fired)) && i < 3}
        />
      ))}

      {skipped.length > 0 && (
        <details className="vr-skipped">
          <summary>{skipped.length} skipped pack(s)</summary>
          <ul>
            {skipped.map((s, i) => (
              <li key={i}>
                <strong>{s.pack_id}</strong>: {s.reason || 'skipped'}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default ViolationsReportView;
